// Component availability at a glance: are the planned parts actually on stock? Prevents the
// classic waste of releasing an order whose parts are missing. One stock read per component
// (orders carry a handful); shortage also puts a warning where the release decision happens.
import React, { useState, useEffect } from 'react';
import { PackageCheck, PackageX } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card } from '../ui';
import { materialStock } from '../../api';

export default function AvailabilityPanel({ user, order, role }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [rows, setRows] = useState(null); // [{materialId, required, onHand, reserved, short}]

  useEffect(() => {
    let alive = true;
    const comps = order.components || [];
    if (!comps.length) { setRows([]); return undefined; }
    Promise.all(comps.map(async (c) => {
      try {
        const res = await materialStock(user, c.materialId, order.plant, role);
        // Stock across locations; the component's own location counts first when set.
        const items = res.items || [];
        const at = c.storageLocation ? items.filter((s) => s.storageLocation === c.storageLocation) : items;
        // RED/GREEN IS DECIDED ON PHYSICAL STOCK (MARD-LABST -> unrestrictedQty), never on
        // availableQty: the adapter defines availableQty as LABST minus OPEN reservation demand
        // (SbxPmSapAdapter.js getMaterialStock), and THIS order's own reservation is part of that
        // demand. Deciding on it made an order whose parts are fully reserved FOR IT read as short
        // — the badge invented a shortage that does not exist (PM code audit 09).
        const onHand = at.reduce((sum, s) => sum + (s.unrestrictedQty || 0), 0);
        const free = at.reduce((sum, s) => sum + (s.availableQty || 0), 0);
        // Still worth showing: open reservation demand, derived. Understates when demand exceeds
        // stock, because the adapter clamps availableQty at 0 — never overstates.
        return {
          materialId: c.materialId,
          required: c.requiredQty,
          onHand,
          reserved: Math.max(0, onHand - free),
          short: onHand < c.requiredQty,
        };
      } catch {
        return { materialId: c.materialId, required: c.requiredQty, onHand: null, reserved: 0, short: false };
      }
    })).then((r) => { if (alive) setRows(r); });
    return () => { alive = false; };
  }, [user, order, role]);

  if (rows === null || rows.length === 0) return null;
  const shorts = rows.filter((r) => r.short);
  const ok = shorts.length === 0;
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  return (
    <Card className="mb-3">
      <div className={`flex items-center gap-2 font-semibold text-sm ${ok
        ? (isDarkMode ? 'text-emerald-300' : 'text-emerald-700')
        : (isDarkMode ? 'text-rose-300' : 'text-rose-600')}`}>
        {ok ? <PackageCheck size={18} /> : <PackageX size={18} />}
        {ok ? t('pm.avail.allCovered') : t('pm.avail.short', { n: shorts.length })}
      </div>
      {!ok && (
        <div className={`text-sm mt-2 space-y-0.5 ${sub}`}>
          {shorts.map((r) => (
            <p key={r.materialId}>
              {r.materialId}: {t('pm.avail.needVsHave', { need: r.required, have: r.onHand ?? '?' })}
              {r.reserved > 0 && <span className="ml-1.5">· {t('pm.comp.resShort')} {r.reserved}</span>}
            </p>
          ))}
          <p className={`text-xs mt-1 ${sub}`}>{t('pm.avail.releaseHint')}</p>
        </div>
      )}
    </Card>
  );
}
